import { useEffect, useState } from 'react';
import { Typography } from 'antd';
import {
  CheckCircleFilled,
  ShopOutlined,
  RobotOutlined,
  CrownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { telegram } from '../../utils/telegram';

const { Text } = Typography;

interface Props {
  businessName: string;
  botUsername: string;
}

export default function StepSuccess({ businessName, botUsername }: Props) {
  const { t } = useTranslation('owner');
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    telegram.hapticFeedback.notification('success');
    telegram.hideMainButton();
  }, []);

  const handleOpenBot = () => {
    telegram.hapticFeedback.impact('light');
    window.open(`https://t.me/${botUsername}`, '_blank');
  };

  const handleClose = () => {
    telegram.close();
  };

  const infoRows = [
    {
      icon: <ShopOutlined />,
      iconColor: '#1677ff',
      label: t('onboarding.success.businessLabel'),
      value: businessName,
    },
    {
      icon: <RobotOutlined />,
      iconColor: '#9254de',
      label: t('onboarding.success.botLabel'),
      value: `@${botUsername}`,
    },
    {
      icon: <CrownOutlined />,
      iconColor: '#fa8c16',
      label: t('onboarding.success.planLabel'),
      value: t('onboarding.success.planValue'),
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bl-bg-page, #f4f5f7)',
        padding: '16px 16px 32px',
      }}
    >
      {/* ─── Success Hero ─────────────────────────────────────────── */}
      <div
        className="bl-animate-in"
        style={{
          position: 'relative',
          borderRadius: 18,
          background:
            'linear-gradient(135deg, #1f8b4c 0%, #27ae60 55%, #2dba71 100%)',
          padding: '24px 18px 26px',
          marginBottom: 14,
          overflow: 'hidden',
          boxShadow: '0 10px 28px rgba(31,139,76,0.30)',
          textAlign: 'center',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -50,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            filter: 'blur(8px)',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            bottom: -60,
            left: -40,
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.10)',
            filter: 'blur(10px)',
          }}
        />

        <div style={{ position: 'relative' }}>
          {/* Check disc */}
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.22)',
              backdropFilter: 'blur(10px)',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 38,
              marginBottom: 14,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28)',
            }}
          >
            <CheckCircleFilled />
          </div>

          {/* Badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(255,255,255,0.22)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.8,
              padding: '4px 10px',
              borderRadius: 999,
              marginBottom: 10,
              backdropFilter: 'blur(8px)',
            }}
          >
            {t('onboarding.success.badge')}
          </div>

          <Text
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: -0.5,
              display: 'block',
              lineHeight: 1.2,
              marginBottom: 6,
            }}
          >
            {t('onboarding.success.title')}
          </Text>
          <Text
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.85)',
              display: 'block',
              lineHeight: 1.45,
              maxWidth: 320,
              margin: '0 auto',
            }}
          >
            {t('onboarding.success.subtitle')}
          </Text>
        </div>
      </div>

      {/* ─── Info card ───────────────────────────────────────────── */}
      <div
        className="bl-animate-in bl-animate-in-delay-1"
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 2px 8px rgba(16,24,40,0.08))',
          padding: '6px 16px',
          marginBottom: 14,
        }}
      >
        {infoRows.map((row, idx) => (
          <div
            key={row.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 0',
              borderBottom:
                idx === infoRows.length - 1
                  ? 'none'
                  : '1px solid var(--bl-divider, #f0f0f0)',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: row.iconColor + '18',
                color: row.iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              {row.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontSize: 11,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  display: 'block',
                  marginBottom: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  fontWeight: 600,
                }}
              >
                {row.label}
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--bl-text-strong, #0f172a)',
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.value}
              </Text>
            </div>
          </div>
        ))}
      </div>

      {/* ─── Actions ─────────────────────────────────────────────── */}
      <div
        className="bl-animate-in bl-animate-in-delay-2"
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <button
          type="button"
          onClick={handleOpenBot}
          onTouchStart={() => setPressed(true)}
          onTouchEnd={() => setPressed(false)}
          onTouchCancel={() => setPressed(false)}
          onMouseDown={() => setPressed(true)}
          onMouseUp={() => setPressed(false)}
          onMouseLeave={() => setPressed(false)}
          style={{
            minHeight: 50,
            padding: '0 22px',
            border: 'none',
            borderRadius: 14,
            background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow:
              '0 6px 18px rgba(22,119,255,0.32), inset 0 1px 0 rgba(255,255,255,0.18)',
            transform: pressed ? 'scale(0.985)' : 'scale(1)',
            transition: 'transform 0.1s ease',
            WebkitTapHighlightColor: 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            outline: 'none',
            letterSpacing: 0.15,
            width: '100%',
          }}
        >
          <span>{t('onboarding.success.openBot')}</span>
          <RightOutlined style={{ fontSize: 13 }} />
        </button>

        <button
          type="button"
          onClick={handleClose}
          style={{
            minHeight: 46,
            padding: '0 22px',
            border: '1px solid var(--bl-divider, #ebebeb)',
            borderRadius: 14,
            background: 'var(--bl-surface, #fff)',
            color: 'var(--bl-text-strong, #0f172a)',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            outline: 'none',
          }}
        >
          {t('onboarding.success.close')}
        </button>
      </div>
    </div>
  );
}
